import { z } from 'zod'
import dayjs, { Dayjs } from 'dayjs'
import { fileSchema } from '@/core/validations/fileSchema.ts'
import { appConfig } from '@/config/app'
import {
  isNumberPositiveInteger,
  isPositiveNumber
} from '@/core/validations/validations.ts'
import { Extension } from '@/core/types/AppConfig.ts'

// state / city / sector viajan como number (id del backend). Se acepta también
// string para tolerar valores en tránsito y se exige un id > 0 (0 = "sin
// seleccionar", análogo al viejo string vacío).
const requiredIdNumber = z.coerce
  .number({ message: 'Requerido' })
  .refine((n) => Number.isFinite(n) && n > 0, { message: 'Requerido' })

export const addressSchema = z.object({
  name: z.string().optional(),
  address: z
    .string({
      message: 'Requerido'
    })
    .nonempty('Requerido'),
  lat: z.string(),
  lng: z.string(),
  isFavorite: z.boolean().optional(),
  referencePoint: z.string().optional(),
  state: requiredIdNumber,
  city: requiredIdNumber,
  sector: requiredIdNumber
})

const acceptedExtensions = [
  ...appConfig.imgExtensions,
  'application/pdf'
] as Extension[]

export const assignArticleFormSchemaBasic = z.object({
  imei: z.string(),
  registerNumber: z.string().nonempty('Requerido'),
  brand: z.string().nonempty('Requerido'),
  model: z.string().nonempty('Requerido'),
  cost: z.string().nonempty('Requerido').refine(isPositiveNumber, {
    message: 'Debe ser un número mayor a 0'
  }),
  _maxCost: z.string().nullable(),
  invoiceNumber: z.string().nonempty('Requerido'),
  _creditLimit: z.string().optional(),
  rentabilityArticleId: z
    .string({ message: 'Requerido' })
    .nonempty('Requerido'),
  payment_extra: z.string().optional(),
  date: z.custom<Dayjs>(),
  termsAndConditions: z
    .boolean({
      message: 'Debe aceptar los términos y condiciones'
    })
    .refine((val) => val, {
      message: 'Debe aceptar los términos y condiciones'
    }),
  insuranceProvider: z.string().nonempty('Requerido'),
  policyNumber: z.string().nonempty('Requerido'),
  rifImage: fileSchema(acceptedExtensions, 1, true),
  idCardImage: fileSchema(acceptedExtensions, 1, true),
  driverLicenseImage: fileSchema(acceptedExtensions, 1, true),
  medicalCertificateFrontImage: fileSchema(acceptedExtensions, 1, false),
  medicalCertificateBackImage: fileSchema(acceptedExtensions, 1, false),
  insurancePolicyImage: fileSchema(acceptedExtensions, 1, true),
  invoiceImage: fileSchema(acceptedExtensions, 1, true),
  originCertificateImage: fileSchema(acceptedExtensions, 1, true),
  letterResidence: fileSchema(acceptedExtensions, 1, false),
  originCertificateNumber: z.string().nonempty('Requerido'),
  originCertificateDate: z
    .custom<Dayjs | null>()
    .refine((val) => val, {
      message: 'Requerido'
    })
    .refine((val) => val && val.isBefore(dayjs()), {
      message: 'La fecha no puede ser mayor a hoy'
    }),
  engineSerial: z.string().nonempty('Requerido'),
  modelYear: z.string().nonempty('Requerido'),
  assembler: z.string().nonempty('Requerido'),
  invoiceDate: z
    .custom<Dayjs | null>()
    .refine((val) => val, {
      message: 'Requerido'
    })
    .refine((val) => val && val.isBefore(dayjs()), {
      message: 'La fecha no puede ser mayor a hoy'
    }),
  seats: z
    .string({ message: 'Requerido' })
    .nonempty('Requerido')
    .refine(isNumberPositiveInteger, {
      message: 'Debe ser un número entero mayor a 0'
    }),
  axes: z.enum(['2', '4', '6'], {
    message: 'Requerido'
  }),
  color: z
    .string({
      message: 'Requerido'
    })
    .nonempty('Requerido'),
  taraKg: z
    .string({ message: 'Requerido' })
    .nonempty('Requerido')
    .refine(isPositiveNumber, {
      message: 'Debe ser un número mayor a 0'
    }),
  loadCapacityKg: z.string().nonempty('Requerido').refine(isPositiveNumber, {
    message: 'Debe ser un número mayor a 0'
  }),
  vehicleClass: z
    .string({
      message: 'Requerido'
    })
    .nonempty('Requerido'),
  clientAddress: addressSchema.optional()
})

export const assignArticleFormSchema = assignArticleFormSchemaBasic

export type AssignArticleFormSchema = z.infer<typeof assignArticleFormSchema>
